import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import {
  ArrowLeft,
  MessageCircle,
  Instagram,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';
import { events, profile } from '../mock';

const TallerPage = () => {
  const { slug } = useParams();
  const ev = events.find((e) => e.id === slug);

  if (!ev) return <Navigate to="/" replace />;

  const waLink = `${profile.whatsapp}?text=${encodeURIComponent(
    ev.ctaWhatsappMessage || `Hola Karina! Quiero saber más sobre "${ev.title}".`
  )}`;

  return (
    <div className="min-h-screen bg-[#F4EDE2] text-[#2E2A23]" data-testid="taller-page">
      {/* Header sticky */}
      <header className="sticky top-0 z-30 bg-[#F4EDE2]/92 backdrop-blur-md border-b border-[#DBCEB4]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="taller-back">
            <span className="w-8 h-8 rounded-full bg-[#B8806E] text-[#FAF4E8] grid place-items-center font-serif text-base leading-none">
              k
            </span>
            <span className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[16px] text-[#2E2A23]">Karina Negrin</span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-[#8A7E6E]">
                Talleres
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#B8806E] hover:text-[#9A6555] transition-colors"
              data-testid="taller-header-cta"
            >
              <MessageCircle size={14} strokeWidth={1.7} />
              <span className="hidden sm:inline">Sumarme</span>
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#8A7E6E] hover:text-[#B8806E] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.7} />
              <span className="hidden sm:inline">Volver al sitio</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div
          aria-hidden
          className="absolute -top-32 right-1/3 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, #EBC8C0 0%, #F4EDE2 60%, transparent 80%)',
          }}
        />
        <div
          aria-hidden
          className="absolute -bottom-24 left-0 w-[420px] h-[420px] rounded-full opacity-35 blur-3xl"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, #C5D2BB 0%, #F4EDE2 60%, transparent 80%)',
          }}
        />

        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 relative">
          <div className="grid lg:grid-cols-[1.1fr,1fr] gap-12 lg:gap-16 items-center">
            <div>
              {ev.status && (
                <span
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] text-[11px] tracking-[0.2em] uppercase text-[#2E2A23] mb-6"
                  data-testid="taller-status"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#8FA08A] animate-pulse" />
                  {ev.status}
                </span>
              )}
              <h1
                className="font-serif text-[44px] sm:text-[56px] lg:text-[72px] leading-[0.98] tracking-[-0.02em] text-[#2E2A23]"
                data-testid="taller-title"
              >
                {ev.title}
              </h1>
              <p className="font-script text-[#B8806E] text-[26px] lg:text-[32px] leading-tight mt-3">
                {ev.tagline}
              </p>

              {/* Meta pills */}
              {ev.meta?.length > 0 && (
                <div className="mt-7 flex flex-wrap gap-2.5" data-testid="taller-meta">
                  {ev.meta.map((m, i) => {
                    const I = Icons[m.icon] || Sparkles;
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] text-[12.5px] text-[#2E2A23]"
                      >
                        <I size={14} strokeWidth={1.7} className="text-[#B8806E]" />
                        {m.label}
                      </span>
                    );
                  })}
                </div>
              )}

              <p className="mt-7 text-[16.5px] leading-[1.75] text-[#2E2A23]/85 font-light max-w-[620px]">
                {ev.shortDescription}
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  data-testid="taller-hero-cta"
                >
                  <MessageCircle size={16} strokeWidth={1.6} />
                  {ev.ctaLabel || 'Quiero sumarme'}
                </a>
                <a
                  href={profile.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                  data-testid="taller-ig-cta"
                >
                  <Instagram size={16} strokeWidth={1.6} />
                  Seguime en IG
                </a>
              </div>
            </div>

            {/* image / flyer */}
            <div className="relative">
              <div
                className="relative rounded-[28px] overflow-hidden border-[6px] border-[#FAF4E8] shadow-[0_30px_70px_-30px_rgba(46,42,35,0.45)]"
                style={{
                  background:
                    ev.imageMode === 'contain' ? ev.imageBg || '#E5EAE0' : undefined,
                }}
                data-testid="taller-image"
              >
                <img
                  src={ev.image}
                  alt={ev.title}
                  className={`w-full ${
                    ev.imageMode === 'contain'
                      ? 'h-auto max-h-[640px] object-contain p-3 sm:p-5'
                      : 'aspect-[4/5] object-cover'
                  }`}
                />
              </div>
              <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-[#FAF4E8] border border-[#DBCEB4] grid place-items-center shadow-md">
                <Sparkles size={22} strokeWidth={1.4} className="text-[#B8806E]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body content (long form) */}
      {ev.longContent?.length > 0 && (
        <section className="pb-20 lg:pb-28" data-testid="taller-body">
          <div className="max-w-[860px] mx-auto px-5 lg:px-8">
            {/* highlights cards if any */}
            {ev.highlights?.length > 0 && (
              <div className="mb-14 grid sm:grid-cols-2 gap-3.5">
                {ev.highlights.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 px-5 py-4 rounded-2xl bg-[#FAF4E8] border border-[#DBCEB4]"
                  >
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#B8806E] flex-none" />
                    <span className="text-[14.5px] leading-[1.6] text-[#2E2A23]/90">
                      {h}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-5 text-[16.5px] leading-[1.85] text-[#2E2A23]/88 font-light">
              {ev.longContent.map((s, i) => {
                if (s.type === 'h') {
                  return (
                    <h2
                      key={i}
                      className="font-serif text-[26px] sm:text-[32px] leading-[1.15] text-[#2E2A23] tracking-[-0.01em] pt-8"
                    >
                      <span className="italic text-[#B8806E]">{s.text}</span>
                    </h2>
                  );
                }
                if (s.type === 'list') {
                  return (
                    <ul key={i} className="space-y-3 pl-1 pt-1">
                      {s.items.map((it, j) => (
                        <li key={j} className="flex items-start gap-3.5">
                          <span className="mt-3 w-1.5 h-1.5 rounded-full bg-[#B8806E] shrink-0" />
                          <span className="text-[16px] leading-[1.75]">{it}</span>
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (s.type === 'sign') {
                  return (
                    <p
                      key={i}
                      className="font-script text-[#B8806E] text-[30px] sm:text-[36px] leading-tight whitespace-pre-line pt-6 text-center"
                    >
                      {s.text}
                    </p>
                  );
                }
                return <p key={i}>{s.text}</p>;
              })}
            </div>

            {/* final CTA card */}
            <div className="mt-16 rounded-[28px] border border-[#DBCEB4] bg-gradient-to-br from-[#F8E7DD] via-[#F4EDE2] to-[#F2E9D9] p-8 sm:p-12 text-center">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#8A7E6E] mb-3">
                Reserva tu lugar
              </div>
              <h3 className="font-serif text-[28px] sm:text-[36px] leading-[1.1] text-[#2E2A23] tracking-[-0.01em]">
                Si sentís que es por <span className="italic text-[#B8806E]">acá</span>,
                escribime.
              </h3>
              <p className="mt-4 text-[15px] leading-[1.75] text-[#2E2A23]/80 font-light max-w-[480px] mx-auto">
                Te respondo personalmente y te paso los detalles de inscripción
                y formas de aporte.
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
                <a
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  data-testid="taller-final-cta"
                >
                  <MessageCircle size={16} strokeWidth={1.6} />
                  {ev.ctaLabel || 'Quiero sumarme'}
                </a>
                <a
                  href={profile.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost"
                >
                  <Instagram size={16} strokeWidth={1.6} />
                  @karinanegrinpsico
                </a>
              </div>
              <p className="mt-7 inline-flex items-center gap-2 text-[11.5px] tracking-[0.18em] uppercase text-[#8A7E6E]">
                <ShieldCheck size={13} strokeWidth={1.7} />
                Espacio cuidado · Sin juicios
              </p>
            </div>
          </div>
        </section>
      )}

      {/* footer */}
      <footer className="border-t border-[#DBCEB4] py-10 bg-[#F4EDE2]">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-[12px] text-[#8A7E6E]">
          <span>© Karina Negrin · {new Date().getFullYear()}</span>
          <Link to="/" className="hover:text-[#B8806E] transition-colors">
            Conocer el resto de la web
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default TallerPage;
